/**
 * Sounds, off by default (Ben, 17 September 2026). Synthesised with Web Audio, so there
 * are no files to download or cache, and a short buzz where the phone allows one.
 *
 * Which sound a change deserves is worked out by `soundFor`, a pure function, so it can
 * be tested without a speaker.
 */

import type { GameEvent, PlayerView } from '@garbage/shared';

export type SoundId = 'yourTurn' | 'cardOffered' | 'youGotIt' | 'cardTaken' | 'wentDown' | 'roundOver' | 'matchOver' | 'reaction';

/**
 * The one sound worth playing for this update, or null. One at most: several at once is
 * noise, so the most important thing that happened to *you* wins.
 */
export function soundFor(events: readonly GameEvent[], view: PlayerView, previous: PlayerView | null): SoundId | null {
  const you = view.you;
  if (events.some((e) => e.type === 'MATCH_WON' || e.type === 'GAME_ABANDONED')) return 'matchOver';
  if (events.some((e) => e.type === 'ROUND_SCORED')) return 'roundOver';

  const closed = events.find((e): e is Extract<GameEvent, { type: 'CLAIM_WINDOW_CLOSED' }> => e.type === 'CLAIM_WINDOW_CLOSED');
  if (closed?.winner === you) return 'youGotIt';

  const yourTurnNow = view.status === 'playing' && !view.claimWindow && view.turn.playerId === you;
  const yourTurnBefore = previous !== null && previous.status === 'playing' && !previous.claimWindow
    && previous.turn.playerId === you;
  if (yourTurnNow && !yourTurnBefore) return 'yourTurn';

  const opened = events.some((e) => e.type === 'CLAIM_WINDOW_OPENED');
  if (opened && view.claimWindow?.youMayClaim) return 'cardOffered';

  if (closed?.winner) return 'cardTaken';
  if (events.some((e) => e.type === 'WENT_DOWN' && e.playerId !== you)) return 'wentDown';
  return null;
}

type Note = { freq: number; at: number; length: number; type?: OscillatorType; gain?: number };

const TUNES: Record<SoundId, Note[]> = {
  // A rising pair: something for you to do.
  yourTurn: [{ freq: 660, at: 0, length: 0.12 }, { freq: 880, at: 0.12, length: 0.2 }],
  // A quick double tap: look, a card.
  cardOffered: [{ freq: 988, at: 0, length: 0.07, type: 'triangle' }, { freq: 988, at: 0.1, length: 0.07, type: 'triangle' }],
  youGotIt: [{ freq: 784, at: 0, length: 0.1 }, { freq: 1047, at: 0.1, length: 0.18 }],
  cardTaken: [{ freq: 523, at: 0, length: 0.09, type: 'triangle', gain: 0.12 }],
  wentDown: [{ freq: 392, at: 0, length: 0.1, type: 'triangle', gain: 0.12 }, { freq: 523, at: 0.09, length: 0.12, type: 'triangle', gain: 0.12 }],
  roundOver: [{ freq: 523, at: 0, length: 0.12 }, { freq: 659, at: 0.12, length: 0.12 }, { freq: 784, at: 0.24, length: 0.25 }],
  matchOver: [
    { freq: 523, at: 0, length: 0.12 }, { freq: 659, at: 0.12, length: 0.12 },
    { freq: 784, at: 0.24, length: 0.12 }, { freq: 1047, at: 0.36, length: 0.4 },
  ],
  reaction: [{ freq: 1175, at: 0, length: 0.06, type: 'sine', gain: 0.1 }],
};

/** Buzz patterns, in ms. Only for things that want your attention. */
const BUZZ: Partial<Record<SoundId, number[]>> = {
  yourTurn: [60],
  cardOffered: [30, 40, 30],
};

type AudioContextLike = AudioContext;
let context: AudioContextLike | null = null;

function audio(): AudioContextLike | null {
  if (context) return context;
  const Ctor = typeof window === 'undefined'
    ? undefined
    : (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return null;
  try {
    context = new Ctor();
  } catch {
    return null;
  }
  return context;
}

/**
 * Phones only let a page make sound after a tap. Call this from a tap — turning sounds
 * on is one — and on the first tap after a reload.
 */
export function unlockAudio(): void {
  const ctx = audio();
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
}

export function playSound(id: SoundId): void {
  const ctx = audio();
  if (ctx) {
    try {
      const start = ctx.currentTime + 0.01;
      for (const note of TUNES[id]) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = note.type ?? 'sine';
        osc.frequency.value = note.freq;
        const peak = note.gain ?? 0.18;
        gain.gain.setValueAtTime(0.0001, start + note.at);
        gain.gain.exponentialRampToValueAtTime(peak, start + note.at + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + note.at + note.length);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start + note.at);
        osc.stop(start + note.at + note.length + 0.05);
      }
    } catch {
      /* a sound is never worth an error */
    }
  }
  const pattern = BUZZ[id];
  if (pattern && typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(pattern);
    } catch {
      /* ignore */
    }
  }
}
